import React, {
	createContext,
	type ReactNode,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { fetchQuery, type RefetchFn, useRelayEnvironment } from 'react-relay';
import type { Subscription, GraphQLTaggedNode } from 'relay-runtime';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import type {
	HydrationRefetchQuery$variables,
	HydrationRefetchQuery,
	JiraJqlViewContext,
	JiraJqlScopeInput,
} from '@atlassian/jira-relay/src/__generated__/HydrationRefetchQuery.graphql';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';

type HydrationQueryContextProps = {
	/**
	 * Whether or not the hydration query is currently fetching
	 */
	hydrationQueryIsFetching: boolean;
	/**
	 * The JQL context for the last completed hydration operation
	 */
	hydrationQueryContextJql: string | undefined;
};

type HydrationQueryProviderProps = {
	refetch: RefetchFn<HydrationRefetchQuery>;
	query: GraphQLTaggedNode;
	contextJql: string;
	hydratedJql: string | undefined;
	children: ReactNode;
	viewContext?: JiraJqlViewContext | null;
	scope?: JiraJqlScopeInput | null;
};

const HydrationQueryContext = createContext<HydrationQueryContextProps>({
	hydrationQueryIsFetching: false,
	hydrationQueryContextJql: '',
});
HydrationQueryContext.displayName = 'HydrationQueryContext';

export const useHydrationQuery = () => useContext(HydrationQueryContext);
export const HydrationQueryProvider = ({
	refetch,
	query,
	contextJql,
	hydratedJql,
	children,
	viewContext,
	scope,
}: HydrationQueryProviderProps): React.JSX.Element => {
	const [hydrationQueryIsFetching, setHydrationQueryIsFetching] = useState(false);
	const cloudId = useCloudId();
	const atlassianAccountId = useAccountId();
	const environment = useRelayEnvironment();
	const inFlightRequest = useRef<Subscription>();
	const inFlightContextJql = useRef<string>();

	useEffect(() => {
		const shouldRefetch = contextJql !== hydratedJql && contextJql !== inFlightContextJql.current;

		if (shouldRefetch) {
			// Prevent race condition from multiple requests in progress
			// (ex. if user clicks quickly on different filters)
			inFlightRequest.current?.unsubscribe();
			inFlightContextJql.current = contextJql;

			let variables: HydrationRefetchQuery$variables;

			if (fg('jira_nin_last_used_jql')) {
				variables = {
					filterAri: '',
					jql: '',
					input: {
						jql: contextJql,
					},
					accountId: atlassianAccountId ?? '',
					includeJira: true,
					includeUser: true,
					cloudId,
					isFilter: false,
					...(fg('jql_builder_add_view_context_to_hydration_query') && { viewContext }),
					scope,
					isPlanReleaseFeatureToggleEnabled: fg('simple-plans-m2-release-feature-toggle'),
				};
			} else {
				variables = {
					filterAri: '',
					jql: contextJql,
					input: null,
					accountId: atlassianAccountId ?? '',
					includeJira: true,
					includeUser: true,
					cloudId,
					isFilter: false,
					...(fg('jql_builder_add_view_context_to_hydration_query') && { viewContext }),
					scope,
					isPlanReleaseFeatureToggleEnabled: fg('simple-plans-m2-release-feature-toggle'),
				};
			}

			setHydrationQueryIsFetching(true);

			// Prefetch data, then update returned variables on complete
			inFlightRequest.current = fetchQuery<HydrationRefetchQuery>(environment, query, variables, {
				fetchPolicy: 'store-or-network',
				networkCacheConfig: {
					metadata: { META_SLOW_ENDPOINT: true },
				},
			}).subscribe({
				complete: () => {
					refetch(variables, {
						fetchPolicy: 'store-only',
						onComplete: () => {
							setHydrationQueryIsFetching(false);
							inFlightContextJql.current = undefined;
						},
					});
				},
				error: (error: Error) => {
					setHydrationQueryIsFetching(false);
					fireErrorAnalytics({
						meta: {
							id: 'hydrateJqlBuilderQuery',
							packageName: 'jiraJqlBuilder',
							teamName: 'empanada',
						},
						error,
						sendToPrivacyUnsafeSplunk: true,
					});
				},
			});
		}
	}, [
		atlassianAccountId,
		cloudId,
		contextJql,
		environment,
		hydratedJql,
		query,
		refetch,
		setHydrationQueryIsFetching,
		viewContext,
		scope,
	]);

	const contextValue: HydrationQueryContextProps = useMemo(
		() => ({
			hydrationQueryIsFetching,
			hydrationQueryContextJql: hydratedJql,
		}),
		[hydrationQueryIsFetching, hydratedJql],
	);

	return (
		<HydrationQueryContext.Provider value={contextValue}>{children}</HydrationQueryContext.Provider>
	);
};
